import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Eye, EyeOff } from 'lucide-react';
import { Station } from '../data/mockData';

interface ResetPasswordDialogProps {
  isOpen: boolean;
  station: Station | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (password: string) => void;
}

export function ResetPasswordDialog({
  isOpen,
  station,
  isSaving,
  onClose,
  onSave,
}: ResetPasswordDialogProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setError('');
    }
  }, [isOpen]);

  const validate = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      return 'Password must include at least one special character.';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match.';
    }

    return '';
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const message = validate();
    if (message) {
      setError(message);
      return;
    }

    setError('');
    onSave(password);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription className="text-gray-400">
            Set a new password for {station?.name ?? 'this station'}.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-gray-800 bg-gray-950/60 px-4 py-3 text-xs text-gray-300">
          Password must be at least 8 characters and include at least one special character.
          Example: Radio@2026
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-sm text-gray-200">
              New Password
            </Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="bg-gray-800 border-gray-700 text-white pr-10"
                placeholder="Enter a new password"
              />
              {password.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-sm text-gray-200">
              Confirm Password
            </Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Confirm the new password"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="border-gray-700 text-black hover:bg-gray-800 hover:text-white"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Update Password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
